import React, { useEffect, useMemo } from 'react';
import { Spinner2, Text2, HeaderText } from '@el8/vital';
import { NavigateFn } from '@reach/router';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import {
  GetPracticeAvailabilityParams,
  useGetPracticeAvailability,
} from '@elation/api-client/src/booking';
import { PhysicianInfo } from 'types';
import { getBookingInfo, updateBookingInfo } from 'modules/booking';
import { usePhysicians } from 'modules/physician';
import { usePractice } from 'modules/practice';
import { intersection } from 'lodash';
import { getPhysiciansWithHypotheticalAvailabilitySet, transformAvailabilities } from './helpers';
import Service2 from '../Service/Service2';

import styles from './PhysicianAvailabilities.less';

interface OwnProps {
  navigate: NavigateFn;
  practiceId: number;
  serviceLocationId: number;
}

const PhysicianAvailabilities = ({
  navigate,
  practiceId,
  serviceLocationId,
}: OwnProps): JSX.Element => {
  const dispatch = useDispatch();
  const [practice] = usePractice(practiceId);
  const [physicians] = usePhysicians(practiceId);
  const {
    calendar,
    selectedPhysicianId,
    selectedAppointmentTypeIds,
    locationPhysiciansWithHypotheticalAvailability,
  } = useSelector(getBookingInfo);

  const requestParams: GetPracticeAvailabilityParams = useMemo(() => {
    return {
      service_location_ids: [serviceLocationId],
      end_date: DateTime.fromObject({}).plus({ days: 7 }).toISODate(),
      start_date: DateTime.fromObject({}).toISODate(),
    };
  }, [serviceLocationId]);

  const queryData = useGetPracticeAvailability(practiceId, requestParams, {
    query: {
      keepPreviousData: true,
    },
  });

  const { data: { data: availabilities } = {}, isLoading, isSuccess } = queryData;

  useEffect(() => {
    if (!locationPhysiciansWithHypotheticalAvailability && availabilities && !isLoading) {
      const filteredPhysicians = getPhysiciansWithHypotheticalAvailabilitySet(availabilities);
      dispatch(
        updateBookingInfo({
          locationPhysiciansWithHypotheticalAvailability: [...filteredPhysicians],
        }),
      );
    }
  }, [availabilities, isLoading, dispatch, locationPhysiciansWithHypotheticalAvailability]);

  useEffect(() => {
    return function clearLocationPhysicians() {
      dispatch(updateBookingInfo({ locationPhysiciansWithHypotheticalAvailability: null }));
    };
  }, [dispatch]);

  const monthRequestParams: GetPracticeAvailabilityParams = useMemo(() => {
    let monthStart;
    if (calendar.activeAppointmentTypeId && calendar.currentMonth) {
      monthStart = calendar.currentMonth;
    } else {
      monthStart = DateTime.fromObject({}).startOf('month').toISODate();
    }

    return {
      end_date: DateTime.fromISO(monthStart).endOf('month').toISODate(),
      service_location_ids: [serviceLocationId],
      start_date: monthStart,
    };
  }, [calendar.activeAppointmentTypeId, calendar.currentMonth, serviceLocationId]);

  const monthQueryData = useGetPracticeAvailability(practiceId, monthRequestParams, {
    query: {
      keepPreviousData: true,
    },
  });

  const {
    data: { data: monthAvailabilities } = {},
    isSuccess: isMonthSuccess,
    isLoading: isMonthLoading,
  } = monthQueryData;

  useEffect(() => {
    if (!isMonthLoading && isMonthSuccess) {
      dispatch(updateBookingInfo({ calendar: { isLoadingAvailabilities: false } }));
    }
  }, [dispatch, isMonthLoading, isMonthSuccess, monthAvailabilities]);

  const getPhysicianAvailabilities = (physician: PhysicianInfo): JSX.Element | JSX.Element[] => {
    const apptTypes = intersection(selectedAppointmentTypeIds, practice?.appointmentTypes || []);

    if (!isSuccess || !locationPhysiciansWithHypotheticalAvailability) {
      return <Spinner2 className={styles.spinner} />;
    }

    if (!locationPhysiciansWithHypotheticalAvailability.includes(physician.id)) {
      return <Text2 className={styles.noAvailabilities}>No availabilities at this time.</Text2>;
    }

    const physicianAvailabilities = apptTypes.map((appointmentTypeId) => {
      const physiciansWeekAvailabilities = availabilities?.filter(
        (availability) =>
          availability.provider_id === physician.id &&
          availability.appointment_type_id === appointmentTypeId &&
          availability.service_location_id === serviceLocationId,
      );

      const physiciansMonthAvailabilities = monthAvailabilities?.filter(
        (monthAvailability) =>
          monthAvailability.provider_id === physician.id &&
          monthAvailability.appointment_type_id === appointmentTypeId &&
          monthAvailability.service_location_id === serviceLocationId,
      );

      if (physiciansWeekAvailabilities.length === 0) {
        return null;
      }

      const formattedWeekAvailabilities = transformAvailabilities(physiciansWeekAvailabilities);

      return (
        <Service2
          appointmentTypeId={appointmentTypeId}
          key={appointmentTypeId}
          navigate={navigate}
          practiceId={practiceId}
          serviceLocationId={Number(serviceLocationId)}
          physicianId={physician.id}
          nextAvailabilities={formattedWeekAvailabilities}
          monthAvailabilities={transformAvailabilities(physiciansMonthAvailabilities)}
        />
      );
    });

    return physicianAvailabilities;
  };

  const renderPhysicianAvailabilities = (physician: PhysicianInfo): JSX.Element | null => {
    if (locationPhysiciansWithHypotheticalAvailability?.includes(physician.id)) {
      return (
        <section className={styles.physicianAvailabilities} key={physician.id}>
          <HeaderText
            className={styles.physicianName}
            tag="div"
            legacyVariant="legacyTitleText"
            sizing="heading-lg"
          >
            {physician.firstName} {physician.lastName}, {physician.credentials}
          </HeaderText>
          {getPhysicianAvailabilities(physician)}
        </section>
      );
    }
    return null;
  };

  const isSelectedPhysicianAvailable =
    locationPhysiciansWithHypotheticalAvailability?.includes(selectedPhysicianId);
  if (selectedPhysicianId && isSelectedPhysicianAvailable) {
    const selectedPhysician = physicians.find((physician) => physician.id === selectedPhysicianId);
    return renderPhysicianAvailabilities(selectedPhysician);
  }

  if (selectedPhysicianId && !isSelectedPhysicianAvailable && !isLoading) {
    return (
      <>
        <Text2 align="center" sizing="lg">
          <strong>No match for your current criteria</strong>
        </Text2>
        <Text2 align="center">
          Try changing your selection for Provider and/or Appointment Type(s)
        </Text2>
      </>
    );
  }

  return <>{physicians.map((physician) => renderPhysicianAvailabilities(physician))}</>;
};

export default PhysicianAvailabilities;
